import { useEffect, useState, useMemo } from 'react';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import { red, green } from '@mui/material/colors';
import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { faWpforms } from '@fortawesome/free-brands-svg-icons';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import axios from 'axios';
import Header from '../Header';

export default function FormsLink(props) {
    const { org, onOrgUpdated, accesstoken, lockSupplier, title, subtitle, description, setDirty } = props;
    const [ supplier, setSupplier ] = useState(lockSupplier || null);
    const [ priceQuote, setPriceQuote ] = useState(null);
    const [ page, setPage ] = useState(0);
    const [ rowsPerPage, setRowsPerPage ] = useState(5);
    const [ forms, setForms ] = useState(null);
    const [ saving, setSaving ] = useState(false);
    const [ savingSuccess, setSavingSuccess ] = useState(null);

    useEffect(() => {
        setPage(0);
        setRowsPerPage(5);
    }, [supplier])

    function fixOrgForms() {
        const f = _.cloneDeep(org.forms);
        _(f).filter(f => f.type === 'price-quote').map(f => f.sections).flatten().map(s => s.fields).flatten().forEach(f => f.variables = f.variables || [uuidv4()]);
        _(f).filter(f => f.type === 'supplier').map(f => f.sections).flatten().map(s => s.fields).flatten().forEach(f => f.variables = f.variables || []);
        return f;
    }

    useEffect(() => {
        const f = fixOrgForms();
        setForms(f);
    }, [org.forms])

    useEffect(() => {
        setSupplier(lockSupplier || null);
        setPriceQuote(null);
        setPage(0);
        setRowsPerPage(5);
    }, [lockSupplier]);

    useEffect(() => {
        if (setDirty) setDirty(!_.isEqual(forms, org.forms));
    }, [forms, org.forms]);

    function handleChangePage(e, newPage) {
        setPage(newPage);
    }

    function handleChangeRowsPerPage(event) {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }

    async function onSave() {
        setSaving(true);

        try {

            const keys = _.keys(forms);
            for (let i = 0 ; i < keys.length ; i++) {
                const key = keys[i];
                if (_.isEqual(forms[key], org.forms[key])) continue;

                console.log(`Saving ${key}, ${forms[key].title}`);
                await axios.post(`/api/organizations/${org.id}/forms/patch/${key}?accesstoken=${accesstoken}`, { sections: forms[key].sections });
            }

            onOrgUpdated({ ...org, forms });
            setSaving(false);
            setSavingSuccess('yes');
        } catch(e) {
            setSaving(false);
            setSavingSuccess('no');
            console.log('Error saving:', e);
        }
        await new Promise(res => setTimeout(res, 3000));
        setSavingSuccess(null);
    }

    const data = useMemo(() => {
        return !supplier || !forms ? [] : _.flatten(forms[supplier].sections
            .map((section, sectionIdx) => section.fields.map((field, fieldIdx) => ({ section, sectionIdx, field, fieldIdx }))))
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [supplier, forms, page, rowsPerPage]);

    const priceQuoteData = useMemo(() => {
        return !priceQuote|| !forms  ? [] : _.flatten(forms[priceQuote].sections
            .map((section, sectionIdx) => section.fields.map((field, fieldIdx) => ({ section, sectionIdx, field, fieldIdx }))));
    }, [priceQuote, forms]);

    return (
        <Box>
            <Header
                icon={faWpforms}
                title={title ? title : 'Forms Link'}
                subtitle={subtitle}
                buttons={[{
                    title: 'Save',
                    icon: savingSuccess === 'yes' ? faCheck : savingSuccess === 'no' ? faXmark : null,
                    iconColor: savingSuccess === 'yes' ? green[500] : savingSuccess === 'no' ? red[500] : null,
                    width: '80px',
                    color: 'monada-button',
                    action: onSave,
                    thinking: saving,
                }]}
            />
            {description ? description : ''}
            <TableContainer fullWidth sx={{ paddingTop: '2px', paddingBottom: '2px' }} size='small'>
                <Table size='small'>
                    <TableHead fullWidth>
                        <TableRow fullWidth>
                            <TableCell sx={{ paddingTop: '2px', paddingBottom: '2px', minWidth: '300px' }} colSpan={2} >
                                <FormControl fullWidth>
                                    <InputLabel id='supplier-label'>Supplier Form</InputLabel>
                                    <Select disabled={!!lockSupplier} labelId='supplier-label' label='Supplier Form' value={supplier} onChange={(e) => setSupplier(e.target.value)}>
                                        {_(forms).pickBy((f, k) => ((f.type === 'supplier' && !f.deleted) || (k === lockSupplier))).map((f, k) => (
                                            <MenuItem
                                                disabled={priceQuote && forms[priceQuote].transportationMethods && f.transportationMethods && _.intersection(forms[priceQuote].transportationMethods, f.transportationMethods).length === 0}
                                                key={k}
                                                value={k}
                                            >
                                                {f.title}
                                            </MenuItem>
                                        )).value()}
                                    </Select>
                                </FormControl>
                            </TableCell>
                            <TableCell sx={{ paddingTop: '2px', paddingBottom: '2px', minWidth: '300px' }} colSpan={2} >
                                <FormControl fullWidth>
                                    <InputLabel id='pricequote-label'>Price Quote Form</InputLabel>
                                    <Select labelId='pricequote-label' label='Price Quote Form' value={priceQuote} onChange={(e) => setPriceQuote(e.target.value)}>
                                        {_(forms).pickBy(f => f.type === 'price-quote' && !f.deleted).map((f, k) => (
                                            <MenuItem
                                                disabled={supplier && forms[supplier].transportationMethods && forms[supplier].transportationMethods.length > 0 && f.transportationMethods && _.intersection(forms[supplier].transportationMethods, f.transportationMethods).length === 0}
                                                key={k} value={k}
                                            >
                                                {f.title}
                                            </MenuItem>
                                        )).value()}
                                    </Select>
                                </FormControl>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={{ paddingTop: '2px', paddingBottom: '2px' }}>
                                Section
                            </TableCell>
                            <TableCell sx={{ paddingTop: '2px', paddingBottom: '2px' }}>
                                Field
                            </TableCell>
                            <TableCell sx={{ paddingTop: '2px', paddingBottom: '2px' }} colSpan={2}>
                                Section - Field
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map(({ section, sectionIdx, field, fieldIdx }, idx) => {
                            return (
                                <TableRow key={`supplier-${sectionIdx}-${fieldIdx}`}>
                                    <TableCell sx={{ paddingTop: '2px', paddingBottom: '2px' }}>
                                        {fieldIdx === 0 || idx === 0 ? section.title : ''}
                                    </TableCell>
                                    <TableCell sx={{ paddingTop: '2px', paddingBottom: '2px' }}>
                                        {field.title}
                                    </TableCell>
                                    <TableCell sx={{ paddingTop: '2px', paddingBottom: '2px' }} colSpan={2}>
                                        <Select
                                            sx={{ maxWidth: '300px' }}
                                            value={(_.intersection(field.variables, _.flatten(priceQuoteData.map(f => f.field.variables))) || [])[0] || ''}
                                            onChange={(e) => {
                                                const f = _.cloneDeep(forms);
                                                const value = e.target.value;

                                                f[supplier].sections[sectionIdx].fields[fieldIdx].variables = _.difference(
                                                    f[supplier].sections[sectionIdx].fields[fieldIdx].variables,
                                                    _.flatten(priceQuoteData.map(f => f.field.variables)) || []);

                                                if (value) {
                                                    f[supplier].sections[sectionIdx].fields[fieldIdx].variables = _.uniq([
                                                        ...f[supplier].sections[sectionIdx].fields[fieldIdx].variables,
                                                        value]);
                                                }
                                                setForms(f);
                                            }}
                                        >
                                            <MenuItem value={''}>-</MenuItem>
                                            {priceQuoteData.map(({ section, sectionIdx, field, fieldIdx }) => (
                                                <MenuItem key={`pricequote-${sectionIdx}-${fieldIdx}`} value={field.variables[0]}>{section.title} - {field.title}</MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                colSpan={4}
                                count={supplier ? _.sumBy(((forms || {})[supplier] || {}).sections, s => s.fields.length) : 0}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                SelectProps={{
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                        native: true,
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    );
}

